import httpClient from '../http/http-client';
import { ILibraryQuestion } from '../library-question-group/library-question-group.model';
import { ISurveyTemplate, IPublishedTemplate, IInstantiateTemplateResult } from './survey-template.model';

// Super-admin management of platform survey templates. Mirrors LibraryQuestionGroupService (the
// endpoint shape is identical) so the same shared authoring dialogs host against it unchanged;
// the only differences are the base path, publish/unpublish, and the absence of the partner
// push/retract/import surface (a template is instantiated on demand, not pushed to clients).
const ADMIN_BASE = 'super-admin/survey-templates';

export class SuperAdminSurveyTemplateService {
  http;

  constructor() {
    this.http = httpClient;
  }

  // ---- templates (question groups) -------------------------------------------

  async getAllWithPagination(page: number, perPage: number = 50, orderBy?: string, order?: 'asc' | 'desc', nameIncludes?: string): Promise<{ count: number, rows: ISurveyTemplate[] }> {
    const params = new URLSearchParams({ page: String(page), perPage: String(perPage) });
    if (order && orderBy) { params.append('orderBy', orderBy); params.append('order', order); }
    if (nameIncludes) params.append('nameIncludes', nameIncludes);
    return this.http.get(`${ADMIN_BASE}/question-groups?${params.toString()}`)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  async getOne(id: string): Promise<ISurveyTemplate> {
    return this.http.get(`${ADMIN_BASE}/question-groups/${id}`)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  async create(template: Partial<ISurveyTemplate>): Promise<ISurveyTemplate> {
    return this.http.post(`${ADMIN_BASE}/question-groups`, template)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  async edit(id: string, template: Partial<ISurveyTemplate>): Promise<ISurveyTemplate> {
    return this.http.patch(`${ADMIN_BASE}/question-groups/${id}`, template)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  async delete(id: string): Promise<{ success: boolean, message: string }> {
    return this.http.delete(`${ADMIN_BASE}/question-groups/${id}`)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  async publish(id: string): Promise<ISurveyTemplate> {
    return this.http.post(`${ADMIN_BASE}/question-groups/${id}/publish`, {})
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  async unpublish(id: string): Promise<ISurveyTemplate> {
    return this.http.post(`${ADMIN_BASE}/question-groups/${id}/unpublish`, {})
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  // Served from the templates router so it is not behind the org subscription/role guards that
  // GET /survey-question-type carries.
  async getQuestionTypes(): Promise<any[]> {
    return this.http.get(`${ADMIN_BASE}/question-types`)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  // ---- the template question bank --------------------------------------------

  async getAllQuestions(promptIncludes?: string): Promise<ILibraryQuestion[]> {
    const params = promptIncludes ? `?promptIncludes=${encodeURIComponent(promptIncludes)}` : '';
    return this.http.get(`${ADMIN_BASE}/questions${params}`)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  async createQuestion(question: Partial<ILibraryQuestion> & { options?: any[] }): Promise<ILibraryQuestion> {
    return this.http.post(`${ADMIN_BASE}/questions`, question)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  async editQuestion(id: string, question: Partial<ILibraryQuestion>): Promise<ILibraryQuestion> {
    return this.http.patch(`${ADMIN_BASE}/questions/${id}`, question)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  async deleteQuestion(id: string): Promise<{ success: boolean, message: string }> {
    return this.http.delete(`${ADMIN_BASE}/questions/${id}`)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  async addOption(questionId: string, option: any) {
    return this.http.post(`${ADMIN_BASE}/questions/${questionId}/options`, option)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  async deleteOption(questionId: string, optionId: string) {
    return this.http.delete(`${ADMIN_BASE}/questions/${questionId}/options/${optionId}`)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  // ---- items and emission rules ----------------------------------------------

  async addItem(groupId: string, item: any) {
    return this.http.post(`${ADMIN_BASE}/question-groups/${groupId}/items`, item)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  async editItem(groupId: string, itemId: string, item: any) {
    return this.http.patch(`${ADMIN_BASE}/question-groups/${groupId}/items/${itemId}`, item)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  async updateItemPages(groupId: string, updates: { id: string, pageNumber: number }[]) {
    return this.http.post(`${ADMIN_BASE}/question-groups/${groupId}/items/update-pages`, { updates })
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  async removeItem(groupId: string, itemId: string) {
    return this.http.delete(`${ADMIN_BASE}/question-groups/${groupId}/items/${itemId}`)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  async createRule(groupId: string, rule: any) {
    return this.http.post(`${ADMIN_BASE}/question-groups/${groupId}/emission-rules`, rule)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  async editRule(groupId: string, ruleId: string, rule: any) {
    return this.http.patch(`${ADMIN_BASE}/question-groups/${groupId}/emission-rules/${ruleId}`, rule)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  async removeRule(groupId: string, ruleId: string) {
    return this.http.delete(`${ADMIN_BASE}/question-groups/${groupId}/emission-rules/${ruleId}`)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }
}

// Client-facing consumption of published templates: list them for the pickers, and instantiate
// one into the caller's own account. Behind ordinary org auth, not super-admin.
const CLIENT_BASE = 'survey-template';

export class SurveyTemplateService {
  http;

  constructor() {
    this.http = httpClient;
  }

  async listPublished(): Promise<IPublishedTemplate[]> {
    return this.http.get(`${CLIENT_BASE}`)
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }

  async instantiate(templateId: string, name?: string): Promise<IInstantiateTemplateResult> {
    return this.http.post(`${CLIENT_BASE}/${templateId}/instantiate`, name ? { name } : {})
      .then(r => r.data).catch(err => { console.log(err); throw err; });
  }
}
